import {
  Button,
  Heading,
  PillsContainer,
  ProductSuggestions,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import SearchGallery from '../../components/molecules/SearchGallery/SearchGallery';
import SearchSlider from '../../components/molecules/SearchSlider/SearchSlider';
import ROUTE_NAME from '../../utils/url-constants';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { DTVendable, FBD_Vendable } from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { useProductSearchConfig } from '../../hooks/useProductSearchConfig';

interface GroupedResults {
  [type: string]: DTVendable[];
}

interface VendableSearchContainerProps {
  onChangeStep?: (step_in: number, data: any) => void;
}

export function VendableSearchContainerFirestore({
  onChangeStep,
}: VendableSearchContainerProps) {
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string[]>([]);
  const [results, setResults] = useState<GroupedResults>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const { shouldAutoLoadProducts, t } = useProductSearchConfig();

  const QUERY_PARAM = 'searchParams';
  const searchParams = new URLSearchParams(window.location.search);

  const { config: appConfig, tenantInfo } = useContext(AppContext);

  // The Algolia container this tenant used to run forces a brand into the URL
  // when SHOW_ONLY_BRAND_PRODUCTS is set. Without the same fallback here,
  // arriving with no brand - a bare /products/search, or a fresh session -
  // listed every brand the tenant owns side by side, which is what a
  // brand-only storefront must never show.
  const brandInUrl = searchParams.get('brand');
  const brand =
    brandInUrl ||
    (appConfig?.VENDABLES?.SHOW_ONLY_BRAND_PRODUCTS
      ? tenantInfo?.name?.toLowerCase() || null
      : null);

  const findResults = useCallback(
    async (searchQuery: string) => {
      setIsLoading(true);
      try {
        let query = FBD_Vendable.query();

        query = query
          .where('mfr', '==', `M:${t('tenantLink')}`)
          .where('published', '==', true);
        const rawVendables = await query.getContents();
        // Exclude products meant only for public API (e.g. listers) from UI search
        let vendables = rawVendables.filter((v) => v.brand !== 'listers');

        // Brands of one tenant share an origin and are told apart by ?brand=.
        // Matched in memory rather than with a where clause: stored values are
        // inconsistently cased (POZIE alongside Pozie, HY-CLOR alongside
        // Hy-Clor) and a Firestore equality would be case sensitive.
        if (brand) {
          const wanted = brand.trim().toLowerCase();
          vendables = vendables.filter(
            (v) => (v.brand ?? '').trim().toLowerCase() === wanted
          );
        }

        // The query argument used to be ignored here, so typing in the search
        // box refetched the same full list. Filter on it rather than leave the
        // input inert.
        const needle = (searchQuery ?? '').trim().toLowerCase();
        if (needle) {
          const terms = needle
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);
          vendables = vendables.filter((v) =>
            terms.some((term) =>
              `${v.title ?? ''} ${v.mpn ?? ''} ${v.category ?? ''}`
                .toLowerCase()
                .includes(term)
            )
          );
        }

        const groupedData = groupResultsByCategory(vendables, 'category');

        if (Object.values(groupedData).length) {
          setResults(groupedData);
          setNoResults(false);
        } else {
          setResults({});
          setNoResults(true);
        }

        return groupedData;
      } finally {
        setIsLoading(false);
      }
    },
    [t, brand]
  );

  useEffect(() => {
    if (shouldAutoLoadProducts) {
      findResults('');
    }
  }, [shouldAutoLoadProducts, findResults]);

  const updateQueryString = (arr: string[]) => {
    navigate({
      search: arr?.length
        ? createSearchParams({
            searchParams: arr.join(',').toLowerCase(),
          }).toString()
        : '',
    });
  };

  const addValue = (value: string) => {
    if (!searchValue.includes(value)) {
      const newArray = [...searchValue, value];
      if (newArray?.length) updateQueryString(newArray);
      if (noResults) updateQueryString([value]);
    }
  };

  const groupResultsByCategory = (
    vendables: DTVendable[],
    property: string
  ) => {
    const groupedData = vendables.reduce((groupedData, currentItem: any) => {
      // category is an array on most records but a plain string on others, and
      // slice(-1) on a string yields its last character - which would group
      // those products under a single letter. Absent would throw outright.
      const raw = currentItem[property];
      const propertyValues = Array.isArray(raw)
        ? raw[raw.length - 1]
        : typeof raw === 'string'
        ? raw
        : undefined;

      if (propertyValues && !groupedData[propertyValues]) {
        groupedData[propertyValues] = [];
      }

      if (propertyValues && groupedData[propertyValues]) {
        groupedData[propertyValues].push(currentItem);
      }

      return groupedData;
    }, {} as Record<string, DTVendable[]>);

    return groupedData;
  };

  const handleSubmitSearch = (event?: { key: string }) => {
    if (!searchInput) return;
    if (event && event.key !== 'Enter') return;

    addValue(searchInput);
    setSearchInput('');
  };

  const handleQuickSearch = (value: string) => {
    addValue(value);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    setSearchInput(event.target.value);
  };

  const handleShowMore = async (value: string) => {
    if (!searchValue.includes(value)) {
      setSearchValue([...searchValue, value]);
      addValue(value);
    }

    const filteredResults = Object.keys(results || {}).reduce((acc, key) => {
      const group = (results || {})[key];
      const matchingItems = group.filter(
        (item) => item.series === value && item.published !== false
      );

      if (matchingItems.length > 0) {
        acc[key] = matchingItems;
      }
      return acc;
    }, {} as GroupedResults);

    setResults(filteredResults);
  };

  const handleDeleteSearchItem = (index: number, value: string) => {
    const newSearchValue = [...searchValue];
    newSearchValue.splice(index, 1);
    setSearchValue(newSearchValue);
    if (newSearchValue) updateQueryString(newSearchValue);
  };

  useEffect(() => {
    if (searchValue.length) {
      findResults(searchValue.join(','))
        .then((data) => console.log('success:', data))
        .catch((err) => console.log(err));
    } else setResults([] as unknown as GroupedResults);
  }, [searchValue]);

  useEffect(() => {
    const paramValues = searchParams.get(QUERY_PARAM) || '';
    if (paramValues.split(', ') !== searchValue) {
      const paramsArray = paramValues.split(',');
      if (paramsArray && paramsArray[0]?.length) setSearchValue(paramsArray);
      else setSearchValue([]);
    }
  }, [window.location.search, QUERY_PARAM]);

  return (
    <>
      {searchValue.length > 0 && (
        <div className="mt-4">
          <PillsContainer
            values={searchValue}
            onClick={handleDeleteSearchItem}
          />
        </div>
      )}
      <div>
        {isLoading ? (
          <LoadingSpinner size="md" />
        ) : (
          <>
            <RenderSearchGallery
              results={results}
              handleQuickSearch={handleQuickSearch}
              onChangeStep={onChangeStep}
              noResults={noResults}
              searchValue={searchValue}
              handleShowMore={handleShowMore}
            />
          </>
        )}
      </div>
    </>
  );
}

export function RenderSearchGallery({
  results,
  handleQuickSearch,
  onChangeStep,
  noResults,
  searchValue,
  handleShowMore,
}: {
  results: GroupedResults | undefined;
  noResults: boolean;
  searchValue: string[];
  onChangeStep: ((step_in: number, data: any) => void) | undefined;
  handleQuickSearch: (value: string) => void;
  handleShowMore: (value: string) => Promise<void>;
}) {
  const { shouldShowSuggestions, shouldShowProducts, config } =
    useProductSearchConfig();
  const navigate = useNavigate();

  const hasSearchValue = searchValue.length > 0;

  return (
    <>
      {noResults && searchValue.length ? (
        <div className="mt-[25px] mb-4">
          <p className="font-nunito font-bold">No products were found.</p>
        </div>
      ) : null}

      {/* Show suggestions when appropriate */}
      {shouldShowSuggestions(hasSearchValue) && (
        <>
          <div className="mt-[25px]">
            <ProductSuggestions submitSearchCallback={handleQuickSearch} />
          </div>
          <div className="mt-8 flex flex-col gap-4">
            {config.HOLDINGS.NEW_REGISTRATION_FLOW.SCAN_QR_CODE && (
              <Button
                kind="primary"
                children="Scan product barcode or QR code"
                onClick={() =>
                  onChangeStep
                    ? onChangeStep(99, null)
                    : navigate(ROUTE_NAME.PRODUCTS_SCAN)
                }
              />
            )}
            {!onChangeStep &&
              config.HOLDINGS.NEW_REGISTRATION_FLOW.ADD_MANUAL_PRODUCT && (
                <>
                  <Heading kind="p" className="mx-auto">
                    or
                  </Heading>
                  <Button
                    kind="outline"
                    children="Add manually"
                    onClick={() => navigate(ROUTE_NAME.PRODUCTS_ADD)}
                  />
                </>
              )}
          </div>
        </>
      )}

      {/* Show products when appropriate */}
      {shouldShowProducts(hasSearchValue) &&
        ((results && Object.keys(results).length < 1) ||
          !results ||
          noResults) && (
          <>
            <div className="mt-8 flex flex-col gap-4">
              {config.HOLDINGS.NEW_REGISTRATION_FLOW.SCAN_QR_CODE && (
                <Button
                  kind="primary"
                  children="Scan product barcode or QR code"
                  onClick={() =>
                    onChangeStep
                      ? onChangeStep(99, null)
                      : navigate(ROUTE_NAME.PRODUCTS_SCAN)
                  }
                />
              )}
              {!onChangeStep &&
                config.HOLDINGS.NEW_REGISTRATION_FLOW.ADD_MANUAL_PRODUCT && (
                  <>
                    <Heading kind="p" className="mx-auto">
                      or
                    </Heading>
                    <Button
                      kind="outline"
                      children="Add manually"
                      onClick={() => navigate(ROUTE_NAME.PRODUCTS_ADD)}
                    />
                  </>
                )}
            </div>
          </>
        )}

      {results
        ? Object.keys(results).map((key, index) => (
            <div key={index}>
              {Object.keys(results).length > 1 ? (
                <div className="mt-8">
                  <SearchSlider
                    title={key.replace(/-/g, ' ')}
                    actionButtonText="Show All"
                    data={results[key]}
                    onClick={handleShowMore}
                    loadMoreInPlace
                  />
                </div>
              ) : (
                <div className="py-2 lg:pt-8">
                  <SearchGallery
                    onChangeStep={onChangeStep}
                    data={results[key]}
                  />
                </div>
              )}
            </div>
          ))
        : null}
    </>
  );
}

export default VendableSearchContainerFirestore;
